import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import config from '../../Config';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

export default function ResetPasswordScreen() {
  const navigate = useNavigate();
  const { token } = useParams();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordRules = {
    length: newPassword.length >= 6,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /\d/.test(newPassword),
    special: /[@$!%*?&^#(){}\[\]-_=+<>.,]/.test(newPassword),
  };

  const isPasswordValid = Object.values(passwordRules).every(Boolean);

  const handleResetPassword = async () => {
    setLoading(true);

    if (!token) {
      Swal.fire({
        icon: 'error',
        title: 'Token Missing',
        text: 'Token not found in URL. Please try again.',
      });
      setLoading(false);
      return;
    }

    if (!isPasswordValid) {
      Swal.fire({
        icon: 'warning',
        title: 'Weak Password',
        text: 'Password does not meet the required rules.',
      });
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      Swal.fire({
        icon: 'warning',
        title: 'Passwords Do Not Match',
        text: 'Please confirm the password correctly.',
      });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${config.apiBaseUrl}/guest/reset-password/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: newPassword }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Password reset successfully!',
        }).then(() => {
          navigate('/login');
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Reset Failed',
          text: data.message || 'Error resetting password.',
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Something Went Wrong',
        text: 'Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const ruleColor = (valid) => ({
    color: valid ? 'green' : 'red',
    fontSize: '14px',
  });

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          display: 'flex',
          flexDirection: 'column',
          maxWidth: { xs: '100%', sm: 400 },
          margin: '0 auto',
          px: { xs: 2, sm: 0 },
          py: { xs: 1, sm: 0 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: { xs: 1.5, sm: 2.5 },
            mt: { xs: 0.5, sm: 1 },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Reset Password
          </Typography>
        </Box>

        <Container maxWidth="sm" sx={{ flex: 1 }}>
          <Box sx={{ mb: 3, mt: 4 }}>
            <Typography sx={{ mb: 1.5, fontWeight: 500 }}>New Password</Typography>
            <TextField
              fullWidth
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              error={newPassword.length > 0 && !isPasswordValid}
              helperText={
                newPassword.length > 0 && !isPasswordValid
                  ? 'Password does not meet the rules.'
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNewPassword((prev) => !prev)}>
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  height: { xs: '48px', sm: '56px' },
                },
              }}
            />

            <Box sx={{ mb: 4 }}>
              <Typography sx={{ mb: 1.5, fontWeight: 500 }}>Confirm Password</Typography>
              <TextField
                fullWidth
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={confirmPassword.length > 0 && confirmPassword !== newPassword}
                helperText={
                  confirmPassword.length > 0 && confirmPassword !== newPassword
                    ? 'Passwords do not match.'
                    : ''
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)}>
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    height: { xs: '48px', sm: '56px' },
                  },
                }}
              />
            </Box>

            <Box sx={{ mt: 1.5, ml: 1 }}>
              <Typography sx={ruleColor(passwordRules.length)}>• Minimum 6 characters</Typography>
              <Typography sx={ruleColor(passwordRules.uppercase)}>
                • At least one uppercase letter
              </Typography>
              <Typography sx={ruleColor(passwordRules.lowercase)}>
                • At least one lowercase letter
              </Typography>
              <Typography sx={ruleColor(passwordRules.number)}>• At least one number</Typography>
              <Typography sx={ruleColor(passwordRules.special)}>
                • At least one special character
              </Typography>
            </Box>
          </Box>

          <Button
            fullWidth
            variant="contained"
            onClick={handleResetPassword}
            disabled={
              !newPassword ||
              !confirmPassword ||
              loading ||
              !isPasswordValid ||
              newPassword !== confirmPassword
            }
            sx={{
              backgroundColor: '#2e7d7b',
              borderRadius: '28px',
              fontWeight: 600,
              height: { xs: '48px', sm: '56px' },
            }}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </Container>

        <Box sx={{ display: 'flex', justifyContent: 'center', pb: 2, mt: 4 }}>
          <Box
            sx={{
              width: 134,
              height: 5,
              backgroundColor: '#000',
              borderRadius: 2.5,
            }}
          />
        </Box>
      </Box>
    </>
  );
}
